import { FacilityListParams } from '@gyym/sdk'
import React from 'react'

import { useSearch } from '@/components/Views/FacilitySearchView/SearchProvider'
import { buildOgImageUrl } from '@/lib/ogiGenerator'
import {
  FACILITY_INDEX_SEARCH_TITLE,
  FACILITY_INDEX_TITLE,
} from '@/modules/Facilities'
import { usePrefectures } from '@/modules/Prefectures'

import { searchQueryConverter } from '../utils'
import { useSearchData } from './useSearchData'

const useSearchPageTitle = (searchParams: FacilityListParams) => {
  const { area, activity, prefecture, city, tag } = useSearchData(searchParams)

  const { isFiltered } = useSearch()

  let title = area
    ? area.name
    : city
    ? city.name
    : prefecture
    ? prefecture.name
    : tag
    ? tag.label
    : ''

  if (activity) {
    title = `${title ? `${title} ` : ''}${activity.name}`
  }

  return {
    searchName: title
      ? title
      : isFiltered
      ? FACILITY_INDEX_SEARCH_TITLE
      : FACILITY_INDEX_TITLE,
    title: title
      ? `${title}のビジター利用(都度利用)可能なジム・スタジオ一覧`
      : isFiltered
      ? FACILITY_INDEX_SEARCH_TITLE
      : FACILITY_INDEX_TITLE,
  }
}

const useSearchBreadcrumbs = (searchParams: FacilityListParams) => {
  const { area, activity, prefecture, city, tag } = useSearchData(searchParams)
  const { isFiltered } = useSearch()
  const { data: prefectures } = usePrefectures()

  const breadcrumbs: {
    label: string
    href:
      | string
      | {
          pathname: string
          query: Record<string, string | string[]>
        }
    as?: string
    shallow?: boolean
  }[] = [
    { label: 'TOP', href: '/' },
    {
      label: FACILITY_INDEX_TITLE,
      href: '/facilities',
      as: '/facilities',
    },
  ]

  if (!area && !activity && !prefecture && !city && !tag && isFiltered) {
    breadcrumbs.push({
      label: '検索結果',
      href: '',
    })

    return breadcrumbs
  }

  if (prefecture) {
    breadcrumbs.push({
      label: prefecture.name,
      ...searchQueryConverter.toLink(
        { prefectureIds: [prefecture.id] },
        prefectures ?? [],
      ),
    })
  }

  if (city && searchParams.cityCode) {
    breadcrumbs.push({
      label: city.name,
      ...searchQueryConverter.toLink(
        { cityCode: `${city.code}` },
        prefectures ?? [],
      ),
    })
  }

  if (area) {
    // find prefecture by id
    const targetPref = prefectures?.find(
      (pref) => pref.id === area.prefectureId,
    )

    if (!targetPref) {
      throw new Error('Prefecture not found')
    }

    breadcrumbs.push({
      label: area.name,
      ...searchQueryConverter.toLink({ areaIds: [area.id] }, prefectures ?? []),
    })
  }

  if (tag) {
    breadcrumbs.push({
      label: tag.label,
      ...searchQueryConverter.toLink({ tags: [tag.value] }, prefectures ?? []),
    })
  }

  if (activity) {
    breadcrumbs.push({
      label: activity.name,
      href: '',
    })
  }

  return breadcrumbs
}

const useSearchKeywords = (searchParams: FacilityListParams) => {
  const { area, activity, prefecture, city, tag } = useSearchData(searchParams)

  return [
    area?.name,
    activity?.name,
    prefecture?.name,
    city?.name,
    tag?.label,
    'gyym',
    'GYYM',
    'ジム',
    'フィットネス',
    'スタジオ',
    'ジム 都度利用',
    'ジム 体験',
    'ジム 入会金無料',
    'ジム 初心者',
  ].filter(Boolean) as string[]
}

export const useSearchPageMeta = (searchParams: FacilityListParams) => {
  const { area, activity, prefecture, city, tag } = useSearchData(searchParams)
  const { title, searchName } = useSearchPageTitle(searchParams)
  const breadcrumbs = useSearchBreadcrumbs(searchParams)
  const keywords = useSearchKeywords(searchParams)

  const isRootFacility =
    searchName === FACILITY_INDEX_TITLE ||
    searchName === FACILITY_INDEX_SEARCH_TITLE

  const ogImageUrl = isRootFacility
    ? buildOgImageUrl`フィットネスジム・スタジオの一覧`
    : buildOgImageUrl`**${searchName}**
のフィットネスジム・スタジオ一覧`

  const prevBreadcrumb = breadcrumbs[breadcrumbs.length - 2]

  const prevPage =
    prevBreadcrumb?.href === '/'
      ? undefined
      : prevBreadcrumb?.href === '/facilities'
      ? {
          label: 'すべて',
          href: '/facilities',
        }
      : prevBreadcrumb

  const place = area
    ? area.name
    : city
    ? city.name
    : prefecture
    ? prefecture.name
    : ''
  const activityName = activity?.name ?? ''
  const tagLabel = tag?.label ?? ''

  return {
    pageMeta: {
      title: `${title} | 会員にならなてくてもいいジム・スタジオのビジター利用(都度利用)専用サービスGYYM`,
      description: `${
        isRootFacility ? '' : `${searchName}の`
      }ビジター利用(都度利用)可能なジム・スタジオを多数掲載。会員にならなてくてもいい、ジム・スタジオのビジター利用(都度利用)専用サービスGYYMでは、ジム初心者やしっかり体験したい方も安心して自分にピッタリなジム・スタジオを探せます。`,
      keywords,
      ogImageUrl,
      heading: title,
      breadcrumbs,
    },
    getParagraph: React.useCallback(
      (count: number) => (
        <>
          {place
            ? `${place}近辺${activityName ? `の${activityName}の` : 'にある'}`
            : tagLabel
            ? `${tagLabel}の`
            : ''}
          フィットネスジム・スタジオで【ビジター利用可（1日利用）】の施設が
          {count}件あります。
          <br />
          会員にならなてくてもいい、ジム・スタジオのビジター利用(都度利用)専用サービスGYYMでは、ジム初心者やしっかり体験したい方も安心して自分にピッタリなジム・スタジオを探せます。
          <br />
          ジム・スタジオのビジター利用(都度利用)専用サービスGYYMでは、ジム初心者やしっかり体験したい方も安心して自分にピッタリなジム・スタジオを探せます。
        </>
      ),
      [place, activityName, tagLabel],
    ),
    isSearchResult: searchName === FACILITY_INDEX_SEARCH_TITLE,
    prevPage,
  }
}
